import { useState, useRef, useMemo } from 'react';
import DocumentHeader from '@/components/DocumentHeader';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from '@simplywallst/ui-core';
import { theme, ScreenSize } from '@simplywallst/ui-core';
import DesktopSidebar from '@/components/DesktopSidebar';
import { PastContainer } from '@/pages/CompanyReport/components/Past';
import { SharePriceNews } from '@/pages/CompanyReport/components/SharePriceNews';
import { ValueContainer } from '@/pages/CompanyReport/components/Value';
import { HealthContainer } from '@/pages/CompanyReport/components/Health';
import { FutureContainer } from '@/pages/CompanyReport/components/Future';
import SummaryContainer from '@/pages/CompanyReport/components/SummaryCompare/containers/SummaryContainer';
import { DividendContainer } from '@/pages/CompanyReport/components/Dividend';
import { ManagementContainer } from '@/pages/CompanyReport/components/Management';
import { OwnershipContainer } from '@/pages/CompanyReport/components/Ownership';
import { InformationContainer } from '@/pages/CompanyReport/components/Information';
import { TwoColumnWithDesktopSidebar } from '@/components/Layout';
import {
  CompanyLabel,
  RemoveButton,
  XYScrollPane,
  CompanyWrapper,
  CompanyHeaderWrapper,
} from './styled';
import NoCompany from '@/pages/CompanyReport/components/CompareCompanies/NoCompany/NoCompany';
import RemoveIconSVG from '@assets/images/icons/legacy/close.svg';
import type { SubNavRoute } from '@/constants/routes';
import If from '@/components/If';
import loadable from '@loadable/component';
import { LoadableChartDefinitionsProvider } from '@/components/ChartDefinitionsContext';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import RecentUpdatesProvider from '../Overview/components/RecentUpdates/context';
import useUser from '@/hooks/useUser';
import { CompanyIDProvider } from './CompanyIDProvider';
import { VisibleSectionsProvider } from '../VisibleSectionsContextProvider';

const PlansInline = loadable(
  () => import('@/components/OurPlans/components/PlansInline/PlansInline')
);
const mobileTheme = theme({
  screenSize: ScreenSize.Mobile, // Todo: Drop this hack
}) as DefaultTheme;

interface Company {
  companyId: string;
  exchangeSymbol: string;
  slug: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  shortName: string;
  restricted: boolean;
  canonicalUrl: string;
}

export interface Props {
  companiesList: Company[];
  onRedirect: (symbols: string) => void;
}

const CompareCompanies = ({ companiesList, onRedirect }: Props) => {
  enum Sections {
    EXECUTIVE_SUMMARY,
    SHARE_PRICE_NEWS,
    VALUATION,
    FUTURE,
    PAST,
    HEALTH,
    DIVIDEND,
    MANAGEMENT,
    OWNERSHIP,
    INFO,
  }
  const [currentSection, setCurrentSection] = useState<Sections>(
    Sections.EXECUTIVE_SUMMARY
  );
  const limits = useCompanyLimits();
  const {
    data: { loggedIn },
  } = useUser();

  const tooltipAnchorRef = useRef<HTMLDivElement | null>(null);

  function handleRemoveCompanyTickerFromCompare(symbol: string) {
    const existing = companiesList.map((company) => company.uniqueSymbol);
    const newSymbol = existing
      .filter((existingSymbol) => existingSymbol !== symbol)
      .join(',');
    onRedirect(newSymbol);
  }

  const concatNames = companiesList
    .map((company) => company.shortName)
    .join(' vs. ');

  function sidebarNavigationOnClick(id?: string | number) {
    if (typeof id !== 'undefined') {
      setCurrentSection(id as Sections);
    }
  }

  const routes = useMemo<SubNavRoute[]>(
    () => [
      {
        label: 'Executive Summary',
        onClick: sidebarNavigationOnClick,
        id: Sections.EXECUTIVE_SUMMARY,
      },
    ],
    []
  );

  const authenticatedRoutes = useMemo<SubNavRoute[]>(
    () => [
      {
        label: 'Executive Summary',
        onClick: sidebarNavigationOnClick,
        id: Sections.EXECUTIVE_SUMMARY,
      },
      {
        label: 'Share Price & News',
        onClick: sidebarNavigationOnClick,
        id: Sections.SHARE_PRICE_NEWS,
      },
      {
        label: 'Valuation',
        onClick: sidebarNavigationOnClick,
        id: Sections.VALUATION,
      },
      {
        label: 'Future Growth',
        onClick: sidebarNavigationOnClick,
        id: Sections.FUTURE,
      },
      {
        label: 'Past Performance',
        onClick: sidebarNavigationOnClick,
        id: Sections.PAST,
      },
      {
        label: 'Financial Health',
        onClick: sidebarNavigationOnClick,
        id: Sections.HEALTH,
      },
      {
        label: 'Dividend',
        onClick: sidebarNavigationOnClick,
        id: Sections.DIVIDEND,
      },
      {
        label: 'Management',
        onClick: sidebarNavigationOnClick,
        id: Sections.MANAGEMENT,
      },
      {
        label: 'Ownership',
        onClick: sidebarNavigationOnClick,
        id: Sections.OWNERSHIP,
      },
      {
        label: 'Company Information',
        onClick: sidebarNavigationOnClick,
        id: Sections.INFO,
      },
    ],
    []
  );

  function handleSectionChange(section: string) {
    if (typeof section !== 'string') return '';
    const sectionNumber = Number(section.replace('Section ', '').split('.')[0]);
    const sectionId =
      sectionNumber === 1
        ? Sections.EXECUTIVE_SUMMARY
        : (authenticatedRoutes[sectionNumber - 1].id as Sections);
    setCurrentSection(sectionId);
  }

  return (
    <TwoColumnWithDesktopSidebar>
      <DocumentHeader title={`Compare - ${concatNames}`} />
      <DesktopSidebar
        routes={loggedIn ? authenticatedRoutes : routes}
        selected={currentSection}
      />
      <XYScrollPane>
        <LoadableChartDefinitionsProvider>
          {companiesList.map((company) => {
            const {
              companyId,
              shortName,
              tickerSymbol,
              uniqueSymbol,
              canonicalUrl,
            } = company;
            const restricted =
              limits.status === 'success' &&
              limits.views === limits.limit &&
              limits.companies.filter((company) => company.id === companyId)
                .length === 0;
            return (
              <CompanyIDProvider key={companyId} canonicalUrl={canonicalUrl}>
                <CompanyWrapper>
                  <CompanyHeaderWrapper>
                    <CompanyLabel>{`${shortName} (${tickerSymbol})`}</CompanyLabel>
                    <RemoveButton
                      onClick={() =>
                        handleRemoveCompanyTickerFromCompare(uniqueSymbol)
                      }
                    >
                      <RemoveIconSVG />
                    </RemoveButton>
                  </CompanyHeaderWrapper>
                  <ThemeProvider theme={mobileTheme}>
                    <RecentUpdatesProvider>
                      <VisibleSectionsProvider>
                        <If
                          conditions={{
                            showSummary:
                              currentSection === Sections.EXECUTIVE_SUMMARY,
                            showRestricted: restricted,
                            showNews:
                              currentSection === Sections.SHARE_PRICE_NEWS,
                            showValuation:
                              currentSection === Sections.VALUATION,
                            showFuture: currentSection === Sections.FUTURE,
                            showPast: currentSection === Sections.PAST,
                            showHealth: currentSection === Sections.HEALTH,
                            showDividend: currentSection === Sections.DIVIDEND,
                            showManagement:
                              currentSection === Sections.MANAGEMENT,
                            showOwnership:
                              currentSection === Sections.OWNERSHIP,
                            showInformation: currentSection === Sections.INFO,
                          }}
                        >
                          {{
                            showSummary: (
                              <SummaryContainer
                                sectionNumber={1}
                                companyId={companyId}
                                onSectionChange={handleSectionChange}
                              />
                            ),
                            showRestricted: (
                              <PlansInline>
                                {limits && (
                                  <p data-cy-id="plan-restriction">{`${limits.views} / ${limits.limit} reports viewed.`}</p>
                                )}
                                <h2>
                                  {RUNTIME_ENV === 'native'
                                    ? 'More stock analysis can be accessed via our other plans.'
                                    : 'Upgrade to unlock your limits.'}
                                </h2>
                              </PlansInline>
                            ),
                            showNews: (
                              <SharePriceNews
                                sectionNumber={2}
                                companyId={companyId}
                              />
                            ),
                            showValuation: (
                              <ValueContainer
                                sectionNumber={3}
                                companyId={companyId}
                              />
                            ),
                            showFuture: (
                              <FutureContainer
                                sectionNumber={4}
                                companyId={companyId}
                              />
                            ),
                            showPast: (
                              <PastContainer
                                sectionNumber={5}
                                companyId={companyId}
                              />
                            ),
                            showHealth: (
                              <HealthContainer
                                sectionNumber={6}
                                companyId={companyId}
                              />
                            ),
                            showDividend: (
                              <DividendContainer
                                sectionNumber={7}
                                companyId={companyId}
                              />
                            ),
                            showManagement: (
                              <ManagementContainer
                                sectionNumber={8}
                                companyId={companyId}
                              />
                            ),
                            showOwnership: (
                              <OwnershipContainer
                                sectionNumber={9}
                                companyId={companyId}
                              />
                            ),
                            showInformation: (
                              <InformationContainer
                                sectionNumber={10}
                                companyId={companyId}
                              />
                            ),
                          }}
                        </If>
                      </VisibleSectionsProvider>
                    </RecentUpdatesProvider>
                  </ThemeProvider>
                </CompanyWrapper>
              </CompanyIDProvider>
            );
          })}
        </LoadableChartDefinitionsProvider>
        <NoCompany
          onSelect={(uniqueSymbol) => {
            onRedirect(
              companiesList
                .map((company) => company.uniqueSymbol)
                .concat([uniqueSymbol])
                .join(',')
            );
          }}
        />
      </XYScrollPane>
      <div ref={tooltipAnchorRef} />
    </TwoColumnWithDesktopSidebar>
  );
};

export default CompareCompanies;
